import { useEffect, useRef, useState } from "react";
import {
  canvaConnectUrl,
  canvaStatus,
  createCanvaSource,
  createSource,
  deleteSource,
  disconnectCanva,
  listSources,
  setSourceRealtime,
  setSourceScheduled,
  setSourceSiteDefault,
  setSourceSlideSeconds,
  syncSource,
  type Source,
} from "../lib/resources.ts";

// Content Sources (admin-only) — import slides from PowerPoint (SharePoint,
// real-time via Graph) and Canva (export, scheduled/manual). Imported slides
// become images in a per-source playlist you assign to a display or schedule.
export default function Sources() {
  const [sources, setSources] = useState<Source[]>([]);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [ppName, setPpName] = useState("");
  const [folder, setFolder] = useState("Announcements");
  const [cvName, setCvName] = useState("");
  const [cvDesign, setCvDesign] = useState("");
  const [creating, setCreating] = useState(false);
  const ppNameRef = useRef<HTMLInputElement>(null);
  const cvNameRef = useRef<HTMLInputElement>(null);
  const cvDesignRef = useRef<HTMLInputElement>(null);

  async function refresh() {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([listSources(), canvaStatus().catch(() => ({ connected: false }))]);
      setSources(s);
      setConnected(c.connected);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load sources");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
    // Surface the OAuth callback result (?canva=connected|failed|denied).
    const q = new URLSearchParams(window.location.search).get("canva");
    if (q === "connected") setNotice("Canva connected.");
    else if (q === "failed") setError("Canva connection failed — try again.");
    else if (q === "denied") setError("Canva connection was cancelled.");
    if (q) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  /**
   * The button stays enabled even with an empty name: a disabled button that
   * silently does nothing reads as a broken app. Say what's missing instead,
   * and put the cursor in the field that needs filling.
   */
  async function addPowerPoint() {
    const name = ppName.trim();
    if (!name) {
      setNotice(null);
      setError("Give the source a name first — e.g. “Weekly Announcements”.");
      ppNameRef.current?.focus();
      return;
    }
    setError(null);
    setCreating(true);
    try {
      await createSource({ kind: "powerpoint", name, folderPath: folder.trim() || "Announcements" });
      setPpName("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create source");
    } finally {
      setCreating(false);
    }
  }

  async function addCanva() {
    const name = cvName.trim();
    const designId = cvDesign.trim();
    if (!name || !designId) {
      setNotice(null);
      setError(
        !name
          ? "Give the Canva source a name first."
          : "Paste the Canva design link or ID.",
      );
      (!name ? cvNameRef : cvDesignRef).current?.focus();
      return;
    }
    setError(null);
    setCreating(true);
    try {
      await createCanvaSource({ name, designId });
      setCvName("");
      setCvDesign("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create Canva source");
    } finally {
      setCreating(false);
    }
  }

  async function unlinkCanva() {
    if (!confirm("Disconnect Canva? Existing Canva sources will stop syncing until reconnected.")) return;
    await disconnectCanva();
    await refresh();
  }

  return (
    <section className="admin-page">
      <div className="page-head">
        <div>
          <h1>Content Sources</h1>
          <p className="muted">
            Import slides from PowerPoint (SharePoint) and Canva. Each source
            builds a dedicated playlist you can schedule or set as a display's
            default.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--error">{error}</div>}
      {notice && <div className="banner banner--ok">{notice}</div>}

      {/* ---- Add PowerPoint ---- */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>PowerPoint (SharePoint)</h2>
        <p className="muted">
          Watches a SharePoint folder and imports the newest deck. Real-time
          available per source.
        </p>
        <div className="page-actions">
          <input
            ref={ppNameRef}
            className="inline-input"
            aria-label="Source name"
            placeholder="Source name (e.g. Weekly Announcements)"
            value={ppName}
            onChange={(e) => setPpName(e.target.value)}
          />
          <input
            className="inline-input"
            placeholder="Folder"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            style={{ maxWidth: "10rem" }}
          />
          <button className="btn" onClick={addPowerPoint} disabled={creating}>
            {creating ? "Adding…" : "+ Add PowerPoint source"}
          </button>
        </div>
      </div>

      {/* ---- Canva ---- */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Canva</h2>
        {connected === null ? (
          <p className="muted">Checking Canva connection…</p>
        ) : !connected ? (
          <>
            <p className="muted">
              Connect the church Canva account once, then import any design.
              (Canva can’t notify us of edits, so Canva sources sync on a
              schedule or on demand.)
            </p>
            <a className="btn" href={canvaConnectUrl}>
              Connect Canva
            </a>
          </>
        ) : (
          <>
            <p className="muted">
              <span className="health health--ok">● connected</span> — paste a
              Canva design link or ID to import it.
              <button className="link-btn" onClick={unlinkCanva} style={{ marginLeft: "0.5rem" }}>
                Disconnect
              </button>
            </p>
            <div className="page-actions">
              <input
                ref={cvNameRef}
                className="inline-input"
                aria-label="Canva source name"
                placeholder="Source name"
                value={cvName}
                onChange={(e) => setCvName(e.target.value)}
              />
              <input
                ref={cvDesignRef}
                className="inline-input"
                aria-label="Canva design link or ID"
                placeholder="Canva design link or ID"
                value={cvDesign}
                onChange={(e) => setCvDesign(e.target.value)}
              />
              <button
                className="btn"
                onClick={addCanva}
                disabled={creating}
              >
                {creating ? "Adding…" : "+ Add Canva source"}
              </button>
            </div>
          </>
        )}
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : sources.length === 0 ? (
        <div className="admin-empty">
          <strong>No sources yet</strong>
          <span>Add a PowerPoint or Canva source above, then Sync.</span>
        </div>
      ) : (
        <div className="display-grid">
          {sources.map((s) => (
            <SourceCard key={s.id} source={s} onChange={refresh} />
          ))}
        </div>
      )}
    </section>
  );
}

function SourceCard({ source, onChange }: { source: Source; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Held as a string so the field can be empty mid-typing without snapping to 0.
  const [seconds, setSeconds] = useState(String(source.slideDurationSeconds));
  // Follow the saved value when it changes underneath us — a refresh after our
  // own save, or another admin's edit arriving on the next list poll.
  useEffect(() => {
    setSeconds(String(source.slideDurationSeconds));
  }, [source.slideDurationSeconds]);
  const isCanva = source.kind === "canva";
  const ref = source.externalRef as { folderPath?: string; designId?: string };

  async function sync() {
    setBusy(true);
    setResult(null);
    setErr(null);
    try {
      const r = await syncSource(source.id);
      // Saying how many kept their description answers the question the button
      // actually raises — "do I have to describe them all again?"
      const kept =
        r.carried && r.slides
          ? r.carried === r.slides
            ? " All kept their descriptions."
            : ` ${r.carried} of ${r.slides} kept their descriptions.`
          : "";
      setResult(
        r.skipped === "unchanged"
          ? "Already up to date."
          : `Imported ${r.slides} slides.${kept}`,
      );
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sync failed");
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  async function toggleAuto() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      if (isCanva) {
        await setSourceScheduled(source.id, source.syncMode !== "scheduled");
      } else {
        await setSourceRealtime(source.id, !source.realtime);
      }
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to change auto-sync");
    } finally {
      setBusy(false);
    }
  }

  /** Save on blur — a keystroke is not a decision, and each save retimes
   *  every imported slide and wakes the screens. */
  async function saveSeconds() {
    const n = Number(seconds);
    if (!Number.isInteger(n) || n < 1 || n > 600) {
      setSeconds(String(source.slideDurationSeconds));
      setErr("Seconds per slide must be a whole number between 1 and 600.");
      return;
    }
    if (n === source.slideDurationSeconds) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      await setSourceSlideSeconds(source.id, n);
      setResult(`Each slide now shows for ${n} seconds.`);
      await onChange();
    } catch (e) {
      setSeconds(String(source.slideDurationSeconds));
      setErr(e instanceof Error ? e.message : "Failed to change slide length");
    } finally {
      setBusy(false);
    }
  }

  async function toggleSiteDefault() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      await setSourceSiteDefault(source.id, !source.isSiteDefault);
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to change default");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete source “${source.name}” and its imported slides?`)) return;
    await deleteSource(source.id);
    await onChange();
  }

  const lastSynced = source.lastSyncedAt
    ? new Date(source.lastSyncedAt).toLocaleString()
    : "never";
  const autoOn = isCanva ? source.syncMode === "scheduled" : source.realtime;
  const autoLabel = isCanva
    ? autoOn
      ? "Auto-sync (every ~15 min)"
      : "Auto-sync off (manual)"
    : autoOn
      ? "Real-time (auto-imports on edit)"
      : "Real-time off (manual)";

  return (
    <div className="display-card card">
      <div className="display-card-head">
        <strong>{source.name}</strong>
        <span className={"health " + (source.status === "error" ? "health--down" : "health--ok")}>
          ● {source.status}
        </span>
      </div>

      <div className="field">
        <span>{isCanva ? "Canva design" : "PowerPoint folder"}</span>
        <code>{(isCanva ? ref.designId : ref.folderPath) ?? "—"}</code>
      </div>

      <label className="field" style={{ flexDirection: "row", gap: "0.5rem", alignItems: "center" }}>
        <input type="checkbox" checked={autoOn} disabled={busy} onChange={toggleAuto} />
        <span>{autoLabel}</span>
      </label>

      <label className="field" style={{ flexDirection: "row", gap: "0.5rem", alignItems: "center" }}>
        <input
          type="number"
          min={1}
          max={600}
          step={1}
          value={seconds}
          disabled={busy}
          style={{ width: "5rem" }}
          onChange={(e) => setSeconds(e.target.value)}
          onBlur={() => void saveSeconds()}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
        <span>
          Seconds per slide
          <br />
          <span className="muted" style={{ fontSize: "0.78rem" }}>
            Applies to every slide this deck imports, now and next time.
          </span>
        </span>
      </label>

      <label className="field" style={{ flexDirection: "row", gap: "0.5rem", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={source.isSiteDefault}
          disabled={busy}
          onChange={toggleSiteDefault}
        />
        <span>
          Show on all displays by default
          {source.isSiteDefault ? " (active site-wide)" : ""}
        </span>
      </label>

      <p className="muted">
        Playlist: <strong>{isCanva ? "Canva" : "PowerPoint"}: {source.name}</strong> · Last
        synced: {lastSynced}
      </p>

      {result && <div className="banner banner--ok">{result}</div>}
      {err && <div className="banner banner--error">{err}</div>}
      {source.status === "error" && source.lastError && !err && (
        <div className="banner banner--error">{source.lastError}</div>
      )}

      <div className="display-card-actions">
        <button className="link-btn" onClick={sync} disabled={busy}>
          {busy ? "Working…" : "Sync now"}
        </button>
        <button className="link-btn link-btn--danger" onClick={remove} disabled={busy}>
          Delete
        </button>
      </div>
    </div>
  );
}
